import { Loader2, Pencil } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateObra } from "@/features/stages/hooks/use-update-obra.ts";
import type { Obra } from "@/features/stages/obra.ts";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { rdoErrorMessage } from "../../utils/errors.ts";

export const TECHNICAL_MANAGER_MAX_LENGTH = 200;

export function TechnicalManagerRow({ obra }: { obra: Obra }) {
	const { has } = usePermissions();
	const canUpdate = has("constructions:update");
	const updateObra = useUpdateObra(obra.id);
	const [editing, setEditing] = useState(false);
	const [value, setValue] = useState(obra.technicalManager ?? "");
	const [error, setError] = useState<string | null>(null);

	function startEditing() {
		setValue(obra.technicalManager ?? "");
		setError(null);
		setEditing(true);
	}

	function cancel() {
		setError(null);
		setEditing(false);
	}

	async function submit(event: React.FormEvent) {
		event.preventDefault();
		const next = value.trim();
		if (next === (obra.technicalManager ?? "")) {
			setEditing(false);
			return;
		}
		try {
			await updateObra.mutateAsync({ technicalManager: next });
			toast.success("Responsável técnico atualizado com sucesso!");
			setEditing(false);
		} catch (err) {
			setError(
				rdoErrorMessage(err, "Não foi possível atualizar o responsável técnico."),
			);
		}
	}

	if (!editing) {
		return (
			<div className="obra-settings-row">
				<div className="obra-settings-row__body">
					<p className="obra-settings-row__label">Responsável técnico</p>
					{obra.technicalManager ? (
						<p className="obra-settings-row__value">{obra.technicalManager}</p>
					) : (
						<p className="obra-settings-row__value obra-settings-row__value--empty">
							Ainda não informado
						</p>
					)}
					<p className="obra-settings-row__note">
						Pré-preenche o responsável técnico de cada novo Relatório de Obra desta obra.
					</p>
				</div>
				{canUpdate && (
					<Button variant="outline" size="sm" onClick={startEditing}>
						<Pencil />
						Editar
					</Button>
				)}
			</div>
		);
	}

	return (
		<div className="obra-settings-row">
			<div className="obra-settings-row__body">
				<Label htmlFor="obra-technical-manager" className="obra-settings-row__label">
					Responsável técnico
				</Label>
			</div>
			<form className="obra-settings-row__form" onSubmit={submit} noValidate>
				<div className="obra-settings-row__field">
					<Input
						id="obra-technical-manager"
						placeholder="Ex.: Eng. Maria Silva — CREA 12345"
						maxLength={TECHNICAL_MANAGER_MAX_LENGTH}
						value={value}
						onChange={(event) => setValue(event.target.value)}
						aria-invalid={!!error}
					/>
					{error && (
						<p role="alert" className="form-error-alert">
							{error}
						</p>
					)}
				</div>
				<div className="obra-settings-row__actions">
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={cancel}
						disabled={updateObra.isPending}
					>
						Cancelar
					</Button>
					<Button type="submit" size="sm" disabled={updateObra.isPending}>
						{updateObra.isPending && (
							<Loader2 className="obra-settings-row__spinner" />
						)}
						Salvar
					</Button>
				</div>
			</form>
		</div>
	);
}
